import Box from "@mui/material/Box";
import ImageIcon from "@mui/icons-material/Image";
import _ from "lodash";
import React, { CSSProperties, Fragment, FunctionComponent, ReactNode } from "react";
import { grey, white } from "designSystem/styles/color";
import FlexRowCenter from "../flex/FlexRowCenter";
import { useResizeObserver } from "designSystem/hooks";

export interface MasonryGridSize {
	height: number;
	width: number;
}

export interface MasonryGridDimensions extends MasonryGridSize {
	column: number;
	x: number;
	y: number;
}

export interface MasonryGridImage extends MasonryGridSize {
	link?: string;
}

export interface MasonryGridInnerProps {
	/** height of container */
	containerHeight: number;

	/** width of container */
	containerWidth: number;

	/** list of images */
	imageList: MasonryGridImage[];

	/** whether to use top-down as the generating pattern for the masonry grid */
	topDown?: boolean;
}

export interface MasonryGridProps {
	/** node to display if no images */
	emptyNode?: ReactNode;

	/** explicit height of grid */
	height?: number;

	/** list of images */
	imageList: MasonryGridImage[];

	/** whether to use top-down as the generating pattern for the masonry grid */
	topDown?: boolean;

	/** explicit width of grid */
	width?: number;
}

/** amount of horizontal padding between images, default to 4 */
const xPadding: number = 4;

/** amount of vertical padding between images, default to 4 */
const yPadding: number = 4;

/**
 * Returns an array of numbers, all the possible column counts for the given number of images
 */
const _calculateColumnPossibilities = (imageCount: number): number[] => {
	// For now, return all numbers between 1 and the number of images
	return _.range(1, imageCount + 1);
};

export const MasonryGridInner: FunctionComponent<MasonryGridInnerProps> = (props) => {
	const { containerHeight, containerWidth, imageList, topDown } = props;

	// total area the current best generated grid uses
	let currentBestArea: number = 0;

	// list of image dimensions for current best generated grid
	let currentBestDimensions: MasonryGridDimensions[] = [];

	const maxHeight = containerHeight - yPadding;

	/**
	 * Calculates the size of the image, using the image's aspect ratio and a fixed width
	 */
	const _calculateImageSize = (image: MasonryGridImage, maxWidth: number): MasonryGridSize => {
		const { height, width } = image;
		const imageAspectRatio = height / width;
		const containerAspectRatio = maxHeight / maxWidth;

		if (imageAspectRatio >= containerAspectRatio) {
			const h = Math.min(height, maxHeight);
			return {
				height: h,
				width: Math.round(h / imageAspectRatio),
			};
		}

		const w = Math.min(width, maxWidth);

		return {
			height: Math.round(w * imageAspectRatio),
			width: w,
		};

		// Maximum aspect ratio is 2:1
		// For now, leaving out cropping, but we are likely to need it later so leaving code here
		/* if (resultObj.width > resultObj.height * 2) {
			resultObj.height = resultObj.width / 2;
		} else if (resultObj.width < resultObj.height / 2) {
			resultObj.height = resultObj.width * 2;
		} */
	};

	/**
	 * Calculates all image dimensions for the given number of columns using the widget's parameters.
	 * Returns a tuple of total area covered by images and an array of dimensions/locations for the images.
	 */
	const _calculateGridWithColumnCount = (columnCount: number): [number, MasonryGridDimensions[]] => {
		const columnWidth = (containerWidth / columnCount) - xPadding;
		const { length } = imageList;

		let area = 0;
		const dimensions: MasonryGridDimensions[] = [];

		let currentColumn = 0;
		let currentImage = 0;
		let x = 0;

		if (topDown) {
			let currentColumnHeight = 0;
			let currentColumnWidth = 0;

			while (currentColumn < columnCount && currentImage < length) {
				const { height, width } = _calculateImageSize(imageList[currentImage], columnWidth);
				const newHeight = currentColumnHeight + height + yPadding;

				if (newHeight <= containerHeight) {
					dimensions.push({
						column: currentColumn,
						height,
						width,
						x,
						y: currentColumnHeight,
					});
					area += height * width;
					currentColumnHeight = newHeight;
					currentColumnWidth = Math.max(currentColumnWidth, width);
					++currentImage;
				} else {
					x += currentColumnWidth + xPadding;
					currentColumnHeight = 0;
					currentColumnWidth = 0;
					++currentColumn;

					if (currentColumn === columnCount) {
						return [-1, dimensions];
					}
				}
			}
		} else {
			const incrementColumn = (curr: number) => (curr < columnCount - 1) ? (curr + 1) : 0;
			const columnHeights = new Array(columnCount)
				.fill(0);
			const maxColumnWidths = new Array(columnCount)
				.fill(0);
			let loopCounter = 0;

			while (currentColumn < columnCount && currentImage < length) {
				const { height, width } = _calculateImageSize(imageList[currentImage], columnWidth);
				const newHeight = columnHeights[currentColumn] + height + yPadding;

				if (newHeight <= containerHeight) {
					dimensions.push({
						column: currentColumn,
						height,
						width,
						x,
						y: columnHeights[currentColumn],
					});
					area += height * width;
					columnHeights[currentColumn] = newHeight;
					maxColumnWidths[currentColumn] = Math.max(maxColumnWidths[currentColumn], width);
					currentColumn = incrementColumn(currentColumn);
					++currentImage;
					loopCounter = 0;
				} else {
					currentColumn = incrementColumn(currentColumn);
					++loopCounter;

					if (loopCounter >= columnCount) {
						return [-1, dimensions];
					}
				}
			}

			// determine starting position of each column
			const columnPositions = maxColumnWidths.map((w) => {
				const pos = x;
				x += w + xPadding;
				return pos;
			});

			// adjust x positions of dimensions
			for (const dim of dimensions) {
				dim.x = columnPositions[dim.column];
			}
		}

		return [area, dimensions];
	};

	/**
	 * Calculates all image dimensions for all images.  Tries every possible column count and keeps the
	 * configuration with the best area.
	 */
	const _calculateAllImageDimensions = () => {
		const possibleColumnNumbers = _calculateColumnPossibilities(imageList.length);

		// reverse indices when left-to-right so the algorithm favors more columns over less (when area is same for multiple columns)
		if (!topDown) {
			possibleColumnNumbers.reverse();
		}

		_.each(possibleColumnNumbers, (columnCount) => {
			const [area, dimensions] = _calculateGridWithColumnCount(columnCount);

			if (area > currentBestArea) {
				currentBestArea = area as number;
				currentBestDimensions = dimensions as MasonryGridDimensions[];
			}
		});
	};

	let imageElements: ReactNode[];

	_calculateAllImageDimensions();

	if (currentBestArea > 0) {
		imageElements = imageList.map(({ link }, i) => {
			const { height, width, x, y } = currentBestDimensions[i];
			const key = "csi_masonry_grid_item" + i;
			const style: CSSProperties = {
				position: "absolute",
				height,
				width,
				left: x,
				top: y,
			};

			if (link) {
				return (
					<img
						key={key}
						src={link}
						style={style}
					/>
				);
			}

			return (
				<ImageIcon
					key={key}
					style={{
						backgroundColor: white[50],
						color: grey[300],
						...style,
					}}
				/>
			);
		});
	}

	return (
		<Fragment>
			{imageElements}
		</Fragment>
	);
};

export const MasonryGrid: FunctionComponent<MasonryGridProps> = (props) => {
	const { emptyNode, height, imageList, topDown, width } = props;
	const { length } = imageList;
	const fixedSize = Boolean(height && width);
	const result = fixedSize ? null : useResizeObserver<HTMLDivElement>();
	const refProps = { ref: result?.ref };
	const containerHeight = height || result?.height;
	const containerWidth = width || result?.width;

	return (
		<Box
			height={height || "100%"}
			position="relative"
			width={width}
			{...refProps}
		>
			{!length && (
				<FlexRowCenter height={1} justifyContent="center">
					{emptyNode}
				</FlexRowCenter>
			)}
			{!!length && containerHeight && containerWidth && (
				<MasonryGridInner
					containerHeight={containerHeight}
					containerWidth={containerWidth}
					imageList={imageList}
					topDown={topDown}
				/>
			)}
		</Box>
	);
};

export default MasonryGrid;